import { useState } from "react";
import axios from "axios";
import { ArrowRight, Mail } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Eyebrow, Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Business Analysis",
  "Product Consulting",
  "Website",
  "Web Application",
  "Mobile Application",
  "UAT",
  "Other",
];

const BUDGETS = [
  "Under ₹50,000",
  "₹50,000 – ₹2,00,000",
  "₹2,00,000 – ₹5,00,000",
  "₹5,00,000 – ₹10,00,000",
  "₹10,00,000+",
];

const INITIAL = {
  name: "",
  email: "",
  company: "",
  project_type: "",
  budget: "",
  description: "",
};

const inputClass =
  "mt-2 border-navy/20 bg-white focus-visible:ring-rust/60";

const FieldError = ({ id, message }) =>
  message ? (
    <p id={id} data-testid={`${id}-error`} className="mt-1.5 text-xs text-rust">
      {message}
    </p>
  ) : null;

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      e.email = "Please enter a valid email address.";
    if (!form.project_type) e.project_type = "Please select a project type.";
    if (!form.budget) e.budget = "Please select a budget range.";
    if (form.description.trim().length < 10)
      e.description = "Please describe your project (at least 10 characters).";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent. I'll get back to you shortly.");
      setForm(INITIAL);
      setErrors({});
    } catch (err) {
      toast.error(
        "Could not send your enquiry. Please email vk@kaizenlabsco.com directly."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="scroll-mt-24 bg-navy py-24 text-cream md:py-32"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-6 md:grid-cols-12 md:px-12">
        <div className="md:col-span-5">
          <Reveal>
            <Eyebrow dark>Contact</Eyebrow>
            <h2 className="mt-5 font-display text-4xl font-semibold leading-[1.15] tracking-tight md:text-5xl">
              Have a product idea or business requirement?
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-cream/70">
              Tell me what you&rsquo;re trying to build, improve or solve. We
              can start by understanding the problem and defining the right
              product approach.
            </p>
            <a
              href="mailto:vk@kaizenlabsco.com"
              data-testid="contact-email-link"
              className="mt-10 inline-flex items-center gap-3 border-b border-rust pb-1.5 text-lg text-cream transition-colors hover:text-rust"
            >
              <Mail className="h-5 w-5 text-rust" />
              vk@kaizenlabsco.com
            </a>
            <p className="mt-8 text-sm text-cream/50">
              Available for remote consulting worldwide.
            </p>
          </Reveal>
        </div>

        <div className="md:col-span-7">
          <Reveal delay={0.1}>
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              noValidate
              className="border border-cream/10 bg-white p-7 text-navy md:p-10"
            >
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <Label htmlFor="contact-name">Name *</Label>
                  <Input
                    id="contact-name"
                    data-testid="contact-form-name-input"
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    placeholder="Your name"
                    className={inputClass}
                    aria-invalid={!!errors.name}
                  />
                  <FieldError id="contact-name" message={errors.name} />
                </div>
                <div>
                  <Label htmlFor="contact-email">Email *</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    data-testid="contact-form-email-input"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder="you@company.com"
                    className={inputClass}
                    aria-invalid={!!errors.email}
                  />
                  <FieldError id="contact-email" message={errors.email} />
                </div>
                <div>
                  <Label htmlFor="contact-company">Company</Label>
                  <Input
                    id="contact-company"
                    data-testid="contact-form-company-input"
                    value={form.company}
                    onChange={(e) => set("company", e.target.value)}
                    placeholder="Company or startup name"
                    className={inputClass}
                  />
                </div>
                <div>
                  <Label htmlFor="contact-project-type">Project Type *</Label>
                  <Select
                    value={form.project_type}
                    onValueChange={(v) => set("project_type", v)}
                  >
                    <SelectTrigger
                      id="contact-project-type"
                      data-testid="contact-form-project-type-select"
                      className={inputClass}
                      aria-invalid={!!errors.project_type}
                    >
                      <SelectValue placeholder="Select project type" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROJECT_TYPES.map((t) => (
                        <SelectItem
                          key={t}
                          value={t}
                          data-testid={`project-type-option-${t.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldError id="contact-project-type" message={errors.project_type} />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="contact-budget">Budget Range *</Label>
                  <Select
                    value={form.budget}
                    onValueChange={(v) => set("budget", v)}
                  >
                    <SelectTrigger
                      id="contact-budget"
                      data-testid="contact-form-budget-select"
                      className={inputClass}
                      aria-invalid={!!errors.budget}
                    >
                      <SelectValue placeholder="Select budget range" />
                    </SelectTrigger>
                    <SelectContent>
                      {BUDGETS.map((b, i) => (
                        <SelectItem
                          key={b}
                          value={b}
                          data-testid={`budget-option-${i}`}
                        >
                          {b}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldError id="contact-budget" message={errors.budget} />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="contact-description">Project Description *</Label>
                  <Textarea
                    id="contact-description"
                    data-testid="contact-form-description-input"
                    value={form.description}
                    onChange={(e) => set("description", e.target.value)}
                    placeholder="What are you trying to build, improve or solve?"
                    rows={5}
                    className={inputClass}
                    aria-invalid={!!errors.description}
                  />
                  <FieldError id="contact-description" message={errors.description} />
                </div>
              </div>
              <button
                type="submit"
                data-testid="contact-form-submit-button"
                disabled={submitting}
                className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-rust px-8 py-4 text-sm font-medium text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 sm:w-auto"
              >
                {submitting ? "Sending…" : "Start a Conversation"}
                {!submitting && (
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
